// Icon
import { Eye, Download, File } from "lucide-react";

// UI Components
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@tanstack/react-router";

// Helpers
import {
  getFileTypeFromUrl,
  getFileExtension,
  formatFileSize,
} from "@/helpers/documentPreview";

// Constants
import { S3_BUCKET_LINK } from "@/components/constants";
import { constructS3FileUrl } from "@/services/s3-file";

interface FileHandleViewerProps {
  fileUrl: string;
  fileName?: string;
  fileSize?: number;
  uploadedBy?: string;
  uploadedAt?: string;
  description?: string;
  showMetadata?: boolean;
  showBtnPreview?: boolean; // New prop to enable enhanced preview
  text?: string;
}

const FileHandleViewer = ({
  fileUrl,
  fileName,
  fileSize,
  uploadedBy,
  uploadedAt,
  description,
  showMetadata = false,
  showBtnPreview = false,
  text,
}: FileHandleViewerProps) => {
  const downloadUrl = `${S3_BUCKET_LINK}${fileUrl}`;
  const fileExtension = getFileExtension(fileUrl);
  const fileType = getFileTypeFromUrl(fileUrl);
  const previewUrl = constructS3FileUrl(fileUrl);

  // Determine display name
  const displayName = fileName || `ເອກະສານ.${fileExtension}`;

  // Get file type badge color
  const getBadgeColor = (type: string): string => {
    switch (type) {
      case "image":
        return "bg-green-100 text-green-800";
      case "video":
        return "bg-purple-100 text-purple-800";
      case "audio":
        return "bg-blue-100 text-blue-800";
      case "document":
        return "bg-red-100 text-red-800";
      case "spreadsheet":
        return "bg-emerald-100 text-emerald-800";
      case "presentation":
        return "bg-orange-100 text-orange-800";
      case "archive":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  return showBtnPreview ? (
    <Link
      to="/document-viewer"
      search={{
        fileUrl: previewUrl,
        fileName: displayName,
        fileSize: fileSize?.toString(),
        uploadedBy,
        uploadedAt,
        description,
      }}
      target="_blank"
      className="text-sm text-blue text-blue-500 underline"
    >
      {text ? text : "ເອກະສານຕິດຂັດ"}
    </Link>
  ) : (
    <Card className="shadow-sm hover:shadow-md transition-shadow w-[22rem]">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 text-white rounded-lg bg-primary">
            <File className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold truncate">ເອກະສານຕິດຂັດ</h3>
              <Badge className={getBadgeColor(fileType)} variant="secondary">
                {fileExtension.toUpperCase()}
              </Badge>
            </div>

            {showMetadata && (
              <div className="space-y-1 text-sm text-gray-600">
                {fileSize && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">ຂະໜາດ:</span>
                    <span>{formatFileSize(fileSize)}</span>
                  </div>
                )}
                {uploadedBy && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">ອັບໂຫຼດໂດຍ:</span>
                    <span>{uploadedBy}</span>
                  </div>
                )}
                {uploadedAt && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">ວັນທີ:</span>
                    <span>{uploadedAt}</span>
                  </div>
                )}
                {description && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium">ລາຍລະອຽດ:</span>
                    <span className="text-gray-700">{description}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="ghost" size="icon" asChild>
              <Link
                to="/document-viewer"
                search={{
                  fileUrl: previewUrl,
                  fileName: displayName,
                  fileSize: fileSize?.toString(),
                  uploadedBy,
                  uploadedAt,
                  description,
                }}
                target="_blank"
              >
                <Eye className="w-4 h-4" />
              </Link>
            </Button>

            <Button variant="ghost" size="icon" asChild>
              <a href={downloadUrl} download>
                <Download className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FileHandleViewer;
